// bmp-size-guide-fit-finder.js — tabs, unit toggle and a rough size
// suggester for the size-guide dialog's own content (blocks/bmp-size-guide.liquid).
//
// Delegated at the document level, not scoped to any one dialog: quick-add
// clones the dialog via cloneNode (assets/bmp-size-guide-portal.js), which
// never re-executes an inline/module script on the clone, and the desktop
// route moves the same live node around the DOM. A document listener keyed by
// data attributes works unchanged for every copy, adopted or original.

(() => {
  const FINDER_SELECTOR = '[data-bmp-sg-fit-finder]';

  const SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
  const FIT_OFFSET = { closer: -1, relaxed: 0, oversized: 1 };

  // Rough unisex relaxed-fit weight bands (lb) for Comfort Colors 1717. No
  // official height/weight chart is published for this blank, so this is
  // presented as a starting estimate only — see the note in the result panel.
  const WEIGHT_BANDS = [
    { max: 145, size: 'S' },
    { max: 170, size: 'M' },
    { max: 195, size: 'L' },
    { max: 220, size: 'XL' },
    { max: 250, size: '2XL' },
    { max: Infinity, size: '3XL' },
  ];

  const CM_PER_IN = 2.54;
  const LB_PER_KG = 2.20462;

  /** @param {number} weightLb */
  function baseSizeFromWeight(weightLb) {
    const band = WEIGHT_BANDS.find((b) => weightLb <= b.max);
    return band ? band.size : '3XL';
  }

  /**
   * @param {{ heightIn: number, weightLb: number, fit: string }} input
   * @returns {string}
   */
  function suggestSize({ heightIn, weightLb, fit }) {
    let index = SIZES.indexOf(baseSizeFromWeight(weightLb));

    // Tall frames need more body length than weight alone predicts.
    if (heightIn >= 74 && index < SIZES.indexOf('L')) {
      index = SIZES.indexOf('L');
    }

    index += FIT_OFFSET[fit] ?? 0;
    index = Math.max(0, Math.min(SIZES.length - 1, index));
    return SIZES[index];
  }

  /** @param {Element} finder */
  function setActiveTab(finder, tab) {
    for (const btn of finder.querySelectorAll('[data-bmp-sg-tab]')) {
      const isActive = btn.dataset.bmpSgTab === tab;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', String(isActive));
    }
    for (const panel of finder.querySelectorAll('[data-bmp-sg-panel]')) {
      panel.hidden = panel.dataset.bmpSgPanel !== tab;
    }
  }

  /** @param {Element} finder */
  function setUnit(finder, unit) {
    for (const btn of finder.querySelectorAll('[data-bmp-sg-unit]')) {
      btn.classList.toggle('is-active', btn.dataset.bmpSgUnit === unit);
    }
    for (const cell of finder.querySelectorAll('[data-bmp-sg-value]')) {
      const value = unit === 'cm' ? cell.dataset.cm : cell.dataset.in;
      if (value) cell.textContent = value;
    }
  }

  /** @param {Element} finder */
  function setMeasurementSystem(finder, system) {
    for (const btn of finder.querySelectorAll('[data-bmp-sg-msys]')) {
      btn.classList.toggle('is-active', btn.dataset.bmpSgMsys === system);
    }
    const imperial = finder.querySelector('[data-bmp-sg-imperial-fields]');
    const metric = finder.querySelector('[data-bmp-sg-metric-fields]');
    if (imperial) imperial.hidden = system !== 'imperial';
    if (metric) metric.hidden = system !== 'metric';
  }

  /** @param {Element} finder */
  function setFitPreference(finder, fit) {
    for (const btn of finder.querySelectorAll('[data-bmp-sg-fit]')) {
      btn.classList.toggle('is-active', btn.dataset.bmpSgFit === fit);
    }
  }

  /** @param {Element} finder */
  function activeFitPreference(finder) {
    const active = finder.querySelector('[data-bmp-sg-fit].is-active');
    return active?.dataset.bmpSgFit || 'relaxed';
  }

  /** @param {Element} finder */
  function activeMeasurementSystem(finder) {
    const active = finder.querySelector('[data-bmp-sg-msys].is-active');
    return active?.dataset.bmpSgMsys || 'imperial';
  }

  /** @param {Element} finder */
  function runSuggestion(finder) {
    const system = activeMeasurementSystem(finder);
    const fit = activeFitPreference(finder);

    const result = finder.querySelector('[data-bmp-sg-result]');
    const error = finder.querySelector('[data-bmp-sg-error]');
    const resultSize = finder.querySelector('[data-bmp-sg-result-size]');

    let heightIn = null;
    let weightLb = null;

    if (system === 'metric') {
      const cm = Number(finder.querySelector('[data-bmp-sg-height-cm]')?.value);
      const kg = Number(finder.querySelector('[data-bmp-sg-weight-kg]')?.value);
      if (cm > 0) heightIn = cm / CM_PER_IN;
      if (kg > 0) weightLb = kg * LB_PER_KG;
    } else {
      const ft = Number(finder.querySelector('[data-bmp-sg-height-ft]')?.value) || 0;
      const inch = Number(finder.querySelector('[data-bmp-sg-height-in]')?.value) || 0;
      const lb = Number(finder.querySelector('[data-bmp-sg-weight-lb]')?.value);
      if (ft > 0) heightIn = ft * 12 + inch;
      if (lb > 0) weightLb = lb;
    }

    if (!heightIn || !weightLb) {
      if (result) result.hidden = true;
      if (error) error.hidden = false;
      return;
    }

    if (error) error.hidden = true;
    if (result && resultSize) {
      resultSize.textContent = suggestSize({ heightIn, weightLb, fit });
      result.hidden = false;
    }
  }

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const tabBtn = target.closest('[data-bmp-sg-tab]');
    if (tabBtn instanceof HTMLElement) {
      const finder = tabBtn.closest(FINDER_SELECTOR);
      if (finder) setActiveTab(finder, tabBtn.dataset.bmpSgTab);
      return;
    }

    const unitBtn = target.closest('[data-bmp-sg-unit]');
    if (unitBtn instanceof HTMLElement) {
      const finder = unitBtn.closest(FINDER_SELECTOR);
      if (finder) setUnit(finder, unitBtn.dataset.bmpSgUnit);
      return;
    }

    const msysBtn = target.closest('[data-bmp-sg-msys]');
    if (msysBtn instanceof HTMLElement) {
      const finder = msysBtn.closest(FINDER_SELECTOR);
      if (finder) setMeasurementSystem(finder, msysBtn.dataset.bmpSgMsys);
      return;
    }

    const fitBtn = target.closest('[data-bmp-sg-fit]');
    if (fitBtn instanceof HTMLElement) {
      const finder = fitBtn.closest(FINDER_SELECTOR);
      if (finder) setFitPreference(finder, fitBtn.dataset.bmpSgFit);
      return;
    }

    const suggestBtn = target.closest('[data-bmp-sg-suggest-btn]');
    if (suggestBtn instanceof HTMLElement) {
      const finder = suggestBtn.closest(FINDER_SELECTOR);
      if (finder) runSuggestion(finder);
    }
  });
})();
